// Field Assessment Form Template
// Based on SOP section 6.2 Assessment Form

const getFieldAssessmentFormTemplate = (t) => ({
  formType: 'field-assessment',
  title: {
    en: 'Field Assessment Form',
    th: 'แบบประเมินพื้นที่เพาะปลูก'
  },
  description: {
    en: 'Standardized form for evaluating SOP compliance and safety in field operations',
    th: 'แบบฟอร์มมาตรฐานสำหรับประเมินการปฏิบัติตามข้อกำหนดและความปลอดภัยในการดำเนินงานในพื้นที่'
  },
  fields: [
    // Basic Information
    {
      name: 'assessmentDate',
      label: {
        en: 'Assessment Date',
        th: 'วันที่ประเมิน'
      },
      type: 'date',
      required: true,
      tooltip: {
        en: 'Date when the field assessment was conducted. Example: 2023-10-15',
        th: 'วันที่ทำการประเมินพื้นที่ ตัวอย่าง: 2023-10-15'
      }
    },
    {
      name: 'assessorName',
      label: {
        en: 'Assessor Name',
        th: 'ชื่อผู้ประเมิน'
      },
      type: 'text',
      required: true,
      tooltip: {
        en: 'Full name of the person conducting the assessment. Example: John Smith',
        th: 'ชื่อ-นามสกุลของผู้ประเมิน ตัวอย่าง: จอห์น สมิธ'
      }
    },
    {
      name: 'fieldLocation',
      label: {
        en: 'Field Location',
        th: 'สถานที่เพาะปลูก'
      },
      type: 'text',
      required: true,
      tooltip: {
        en: 'Specific location or field identifier. Example: Field Block A - Northern Section',
        th: 'สถานที่เฉพาะหรือรหัสพื้นที่ ตัวอย่าง: พื้นที่บล็อก A - ส่วนทางเหนือ'
      }
    },
    {
      name: 'cropStage',
      label: {
        en: 'Crop Stage',
        th: 'ระยะการเจริญเติบโตของพืช'
      },
      type: 'select',
      options: [
        { value: 'seedling', label: { en: 'Seedling', th: 'ต้นกล้า' } },
        { value: 'vegetative', label: { en: 'Vegetative', th: 'ระยะใบ' } },
        { value: 'flowering', label: { en: 'Flowering', th: 'ระยะออกดอก' } },
        { value: 'harvest-ready', label: { en: 'Harvest Ready', th: 'พร้อมเก็บเกี่ยว' } }
      ],
      required: true,
      tooltip: {
        en: 'Current growth stage of the hemp plants. Select the most accurate stage.',
        th: 'ระยะการเจริญเติบโตปัจจุบันของต้นกัญชง เลือกระยะที่แม่นยำที่สุด'
      }
    },

    // Safety Checks Section
    {
      name: 'sectionHeader_safety',
      label: {
        en: '=== Safety Checks ===',
        th: '=== การตรวจสอบความปลอดภัย ==='
      },
      type: 'header'
    },
    {
      name: 'ppeUsage',
      label: {
        en: 'PPE Usage Compliance',
        th: 'การปฏิบัติตามการใช้อุปกรณ์ป้องกันส่วนบุคคล'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Evaluate how well workers are using required Personal Protective Equipment. Excellent: All workers properly using PPE. Poor: Many workers not wearing required PPE.',
        th: 'ประเมินว่าผู้ปฏิบัติงานใช้อุปกรณ์ป้องกันส่วนบุคคลที่กำหนดไว้อย่างไร ยอดเยี่ยม: ผู้ปฏิบัติงานทุกคนใช้ PPE อย่างเหมาะสม แย่: ผู้ปฏิบัติงานหลายคนไม่สวม PPE ที่กำหนด'
      }
    },
    {
      name: 'ppeIssues',
      label: {
        en: 'PPE Issues Found',
        th: 'ปัญหาด้าน PPE ที่พบ'
      },
      type: 'textarea',
      rows: 3,
      placeholder: {
        en: 'Describe any PPE compliance issues observed...',
        th: 'อธิบายปัญหาด้านการปฏิบัติตาม PPE ที่พบ...'
      },
      tooltip: {
        en: 'Detail any specific PPE issues such as missing equipment, improper use, or damaged gear. Example: Worker #12 not wearing gloves during chemical application.',
        th: 'ระบุปัญหาเฉพาะด้าน PPE เช่น อุปกรณ์ขาดหาย ใช้ไม่เหมาะสม หรืออุปกรณ์เสียหาย ตัวอย่าง: ผู้ปฏิบัติงาน #12 ไม่สวมถุงมือขณะใช้สารเคมี'
      }
    },
    {
      name: 'equipmentSafety',
      label: {
        en: 'Equipment Safety Compliance',
        th: 'การปฏิบัติตามความปลอดภัยของอุปกรณ์'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Assess the safety condition and proper use of agricultural equipment. Excellent: All equipment properly maintained and operated. Critical: Equipment with serious safety defects or misuse.',
        th: 'ประเมินสภาพความปลอดภัยและการใช้อุปกรณ์เกษตรกรรมอย่างเหมาะสม ยอดเยี่ยม: อุปกรณ์ทั้งหมดบำรุงรักษาและใช้งานอย่างเหมาะสม วิกฤต: อุปกรณ์มีข้อบกพร่องด้านความปลอดภัยร้ายแรงหรือใช้งานผิดวิธี'
      }
    },
    {
      name: 'chemicalHandling',
      label: {
        en: 'Chemical Handling Compliance',
        th: 'การปฏิบัติตามการจัดการสารเคมี'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Evaluate compliance with chemical handling procedures. Excellent: Perfect storage, labeling, and application. Critical: Mislabeling, improper mixing, or unsafe storage.',
        th: 'ประเมินการปฏิบัติตามขั้นตอนการจัดการสารเคมี ยอดเยี่ยม: การจัดเก็บ การติดฉลาก และการใช้งานอย่างสมบูรณ์แบบ วิกฤต: ฉลากผิด การผสมผสานไม่เหมาะสม หรือการจัดเก็บไม่ปลอดภัย'
      }
    },

    // Cultivation Compliance Section
    {
      name: 'sectionHeader_cultivation',
      label: {
        en: '=== Cultivation Compliance ===',
        th: '=== การปฏิบัติตามการเพาะปลูก ==='
      },
      type: 'header'
    },
    {
      name: 'irrigationCompliance',
      label: {
        en: 'Irrigation System Compliance',
        th: 'การปฏิบัติตามระบบการให้น้ำ'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Assess irrigation system maintenance and water usage efficiency. Excellent: Well-maintained systems with optimal water usage. Poor: Broken sprinklers or wasteful watering practices.',
        th: 'ประเมินการบำรุงรักษาระบบการให้น้ำและการใช้น้ำอย่างมีประสิทธิภาพ ยอดเยี่ยม: ระบบบำรุงรักษาอย่างดีด้วยการใช้น้ำที่เหมาะสม แย่: หัวพ่นน้ำเสียหรือพฤติกรรมการให้น้ำที่สิ้นเปลือง'
      }
    },
    {
      name: 'fertilizerApplication',
      label: {
        en: 'Fertilizer Application Compliance',
        th: 'การปฏิบัติตามการใส่ปุ๋ย'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Evaluate proper fertilizer application techniques and timing. Excellent: Precise measurements and appropriate scheduling. Critical: Over-application or use of banned substances.',
        th: 'ประเมินเทคนิคการใส่ปุ๋ยและการจับเวลาอย่างเหมาะสม ยอดเยี่ยม: การวัดปริมาณอย่างแม่นยำและการจัดกำหนดการอย่างเหมาะสม วิกฤต: การใส่ปุ๋ยมากเกินไปหรือใช้สารต้องห้าม'
      }
    },
    {
      name: 'pestControl',
      label: {
        en: 'Pest Control Methods',
        th: 'วิธีการควบคุมแมลงศัตรูพืช'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Assess effectiveness of biological pest control methods. Excellent: Successful IPM implementation. Critical: Reliance on prohibited chemicals or ineffective methods.',
        th: 'ประเมินประสิทธิภาพของวิธีการควบคุมแมลงศัตรูพืชทางชีวภาพ ยอดเยี่ยม: การดำเนินการ IPM สำเร็จ วิกฤต: พึ่งพาสารเคมีต้องห้ามหรือวิธีการที่ไม่มีประสิทธิภาพ'
      }
    },

    // Emergency Preparedness Section
    {
      name: 'sectionHeader_emergency',
      label: {
        en: '=== Emergency Preparedness ===',
        th: '=== การเตรียมพร้อมสำหรับเหตุฉุกเฉิน ==='
      },
      type: 'header'
    },
    {
      name: 'emergencyResponse',
      label: {
        en: 'Emergency Response Readiness',
        th: 'ความพร้อมในการตอบสนองเหตุฉุกเฉิน'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คำถาม)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Evaluate emergency response equipment and training. Excellent: Well-trained staff, complete emergency kits, and clear procedures. Critical: Missing equipment or untrained personnel.',
        th: 'ประเมินอุปกรณ์การตอบสนองเหตุฉุกเฉินและการฝึกอบรม ยอดเยี่ยม: บุคลากรได้รับการฝึกอบรมอย่างดี อุปกรณ์ฉุกเฉินครบถ้วน และขั้นตอนชัดเจน วิกฤต: อุปกรณ์ขาดหายหรือบุคลากรไม่ได้รับการฝึกอบรม'
      }
    },
    {
      name: 'simulationPerformance',
      label: {
        en: 'Simulation Drill Performance',
        th: 'ผลการฝึกซ้อมจำลองสถานการณ์'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (4 points)', th: 'ยอดเยี่ยม (4 คะแนน)' } },
        { value: 'good', label: { en: 'Good (3 points)', th: 'ดี (3 คะแนน)' } },
        { value: 'fair', label: { en: 'Fair (2 points)', th: 'พอใช้ (2 คะแนน)' } },
        { value: 'poor', label: { en: 'Poor (1 point)', th: 'แย่ (1 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0 points)', th: 'วิกฤต (0 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Assess performance during recent emergency simulation exercises. Excellent: Quick, coordinated response with no confusion. Critical: Slow response, confusion, or failure to follow procedures.',
        th: 'ประเมินผลการดำเนินการในแบบฝึกหัดจำลองสถานการณ์ฉุกเฉินล่าสุด ยอดเยี่ยม: การตอบสนองอย่างรวดเร็ว การประสานงานดี และไม่มีความสับสน วิกฤต: การตอบสนองช้า ความสับสน หรือการไม่ปฏิบัติตามขั้นตอน'
      }
    },

    // Overall Scoring Section
    {
      name: 'sectionHeader_scoring',
      label: {
        en: '=== Overall Scoring ===',
        th: '=== การให้คะแนนโดยรวม ==='
      },
      type: 'header'
    },
    {
      name: 'totalScore',
      label: {
        en: 'Total Score (Maximum 40 points)',
        th: 'คะแนนรวม (สูงสุด 40 คะแนน)'
      },
      type: 'number',
      min: 0,
      max: 40,
      required: true,
      tooltip: {
        en: 'Calculated sum of all assessment categories. Maximum 40 points. Critical (0-15): Immediate action required. Major (16-25): Corrective action within 1 week. Moderate (26-35): Monitor progress. Excellent (36-40): Best practices maintained.',
        th: 'ผลรวมที่คำนวณจากหมวดหมู่การประเมินทั้งหมด สูงสุด 40 คะแนน วิกฤต (0-15): ต้องดำเนินการทันที สำคัญ (16-25): มาตรการแก้ไขภายใน 1 สัปดาห์ พอใช้ (26-35): ติดตามความก้าวหน้า ยอดเยี่ยม (36-40): รักษาแนวทางปฏิบัติที่ดีที่สุด'
      }
    },
    {
      name: 'scoreCategory',
      label: {
        en: 'Score Category',
        th: 'หมวดหมู่คะแนน'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (36-40 points)', th: 'ยอดเยี่ยม (36-40 คะแนน)' } },
        { value: 'moderate', label: { en: 'Moderate (26-35 points)', th: 'พอใช้ (26-35 คะแนน)' } },
        { value: 'major', label: { en: 'Major (16-25 points)', th: 'สำคัญ (16-25 คะแนน)' } },
        { value: 'critical', label: { en: 'Critical (0-15 points)', th: 'วิกฤต (0-15 คะแนน)' } }
      ],
      required: true,
      tooltip: {
        en: 'Classification based on total score. Determines required follow-up actions and timeline.',
        th: 'การจัดประเภทตามคะแนนรวม กำหนดการดำเนินการติดตามและระยะเวลาที่จำเป็น'
      }
    },

    // Corrective Actions Section
    {
      name: 'sectionHeader_actions',
      label: {
        en: '=== Corrective Actions ===',
        th: '=== มาตรการแก้ไข ==='
      },
      type: 'header'
    },
    {
      name: 'correctiveActions',
      label: {
        en: 'Corrective Actions Required',
        th: 'มาตรการแก้ไขที่จำเป็น'
      },
      type: 'textarea',
      rows: 6,
      placeholder: {
        en: 'List specific corrective actions required based on the assessment...',
        th: 'ระบุมาตรการแก้ไขเฉพาะที่จำเป็นตามผลการประเมิน...'
      },
      tooltip: {
        en: 'Detail specific actions needed to address identified deficiencies. Include responsible persons, timelines, and resources required. Example: Replace broken PPE within 3 days - Assigned to Safety Officer.',
        th: 'ระบุการดำเนินการเฉพาะที่จำเป็นเพื่อแก้ไขข้อบกพร่องที่พบ รวมถึงบุคคลที่รับผิดชอบ ระยะเวลา และทรัพยากรที่จำเป็น ตัวอย่าง: แทนที่ PPE ที่เสียหายภายใน 3 วัน - มอบหมายให้เจ้าหน้าที่ความปลอดภัย'
      }
    },
    {
      name: 'actionDate',
      label: {
        en: 'Action Due Date',
        th: 'วันครบกำหนดดำเนินการ'
      },
      type: 'date',
      tooltip: {
        en: 'Deadline for completing required corrective actions. Example: 2023-10-30',
        th: 'กำหนดเวลาสำหรับการดำเนินการแก้ไขที่จำเป็น ตัวอย่าง: 2023-10-30'
      }
    },
    {
      name: 'trainerNotes',
      label: {
        en: 'Trainer Notes & Simulation Comments',
        th: 'หมายเหตุของผู้ฝึกและข้อคิดเห็นจากการจำลองสถานการณ์'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'Additional trainer observations and simulation performance notes...',
        th: 'ข้อสังเกตเพิ่มเติมจากผู้ฝึกและหมายเหตุเกี่ยวกับผลการจำลองสถานการณ์...'
      },
      tooltip: {
        en: 'Record observations from simulation exercises and trainer insights. Include both positive performances and areas for improvement. Example: Team responded quickly to spill scenario but lacked communication.',
        th: 'บันทึกข้อสังเกตจากการจำลองสถานการณ์และการให้ข้อมูลเชิงลึกจากผู้ฝึก รวมถึงผลการดำเนินงานที่ดีและพื้นที่ที่ต้องปรับปรุง ตัวอย่าง: ทีมตอบสนองอย่างรวดเร็วต่อสถานการณ์หกเทแต่ขาดการสื่อสาร'
      }
    }
  ]
});

export default getFieldAssessmentFormTemplate;